import { createFileRoute } from '@tanstack/react-router';
import { levelsQuery } from '~/util/queries/public/levels';
import { recordLevelStatsQuery } from '~/util/queries/staff/stats';
import { PlacedLevel } from '~/util/types/levels';
import { seo } from '~/util';
import { useAredleStore } from '~/util/stores/aredle';
import {
    dailyAtTimestamp,
    enrichLevels,
    getDayKey,
    pickDailyLevel,
} from './-shared';
import { AredleHome } from './-Home';
import { AredleRound } from './-Round';

export const Route = createFileRoute('/games/aredle')({
    loader: async ({ context: { queryClient } }) => {
        const dayKey = getDayKey(new Date());

        const [levels, stats, snapshot] = await Promise.all([
            queryClient.fetchQuery(
                levelsQuery({
                    list: 'classic',
                    exclude_pending: true,
                    exclude_removed: true,
                }),
            ),
            queryClient.fetchQuery(recordLevelStatsQuery({ list: 'classic' })),
            // The list frozen at start of UTC day, and selection uses this so answer identity can't change when levels move
            queryClient
                .fetchQuery(
                    levelsQuery({
                        list: 'classic',
                        exclude_pending: true,
                        exclude_removed: true,
                        at: dailyAtTimestamp(dayKey),
                    }),
                )
                .catch(() => null),
        ]);

        const dailyAnswer = snapshot
            ? pickDailyLevel(
                  enrichLevels(snapshot as PlacedLevel[], stats),
                  dayKey,
              )
            : null;

        return {
            levels: enrichLevels(levels as PlacedLevel[], stats),
            dailyAnswer,
            dailyDayKey: dayKey,
        };
    },
    head: () => ({
        meta: seo({
            title: 'AREDLE',
            description:
                'Guess the Extreme Demon. Each guess gives information about its placement, victors, enjoyment and tags compared to the hidden levels info.',
        }),
    }),
    component: AredleLayout,
});

function AredleLayout() {
    const {
        data: { currentMode },
    } = useAredleStore();

    return currentMode == null ? <AredleHome /> : <AredleRound />;
}
